<template>
<div class="products-list" :data-show="show">
  <div class="d-f">
    <Button class="no-border-radius white f-w bold" icon="plusCircle" @click="addProducts = true">Add product</Button>
    <Button class="no-border-radius white f-w bold" icon="plusCircle" @click="addCustomProduct = true">Add via link</Button>
  </div>
  <div class="products">
    <template v-if="products.length > 0">
      <transition-group :name="firstRender ? '' : 'product-transition'" tag="div">
        <product-item
          v-for="(product, index) in products"
          :product="product"
          :key="product.id"
          :index="index"
          :range="products.length - 1"
          :showTime="showTime"
          @move="moveProduct($event, index)"
          @gotoInVideo="$emit('gotoInVideo', $event)"
        />
      </transition-group>
    </template>
    <template v-else>
      <template v-if="fetching">
        <div class="loading-spinner m-a-a"></div>
      </template>
      <template v-else>
        <div class="d-f ai-c jc-c f-1 ta-c p-a-4 page-placeholder-text">
          <p>No products added yet.</p>
        </div>
      </template>
    </template>
  </div>

  <portal to="modals">
    <product-search :show="addProducts" @close="addProducts = false" />
    <product-import :show="addCustomProduct" @close="addCustomProduct = false" />
  </portal>
</div>
</template>

<script>

import ProductSearch from '@/components/Products/ProductSearch.vue';
import ProductImport from '@/components/Products/ProductImport.vue';
import ProductItem from './ProductItem.vue';

export default {
  props: {
    showTime: {
      type: Boolean,
      default: false,
    },
    show: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    ProductItem,
    ProductSearch,
    ProductImport,
  },
  computed: {
    /**
     * Return a list of products from the event stream
     * @returns {Array}
     */
    eventProducts() {
      return this.$store.getters['Products/eventProducts'];
    },
    /**
     * Return the current broadcast in store
     * @returns {?Object}
     */
    broadcast() {
      return this.$store.getters['Broadcast/current'];
    },
    /**
     * Return a list of products connected to the broadcast
     * and map them with allGoods from Products store
     * @returns {Array}
     */
    products() {
      const { liveshoppingEnabled } = this;

      if (liveshoppingEnabled) {
        return this.$store.getters['Products/broadcastProducts'];
      }

      const { broadcast: { event_details: { shopping: { products } } } } = this;
      const order = {};
      products.forEach((a, i) => { order[a] = i; });
      const allGoods = this.$store.getters['Products/allGoods'];
      return allGoods.filter(good => products.includes(good.id))
        .map((good) => {
          const price = good.prices ? good.prices.find(p => p.active) : null;
          return {
            id: good.id,
            title: good.name,
            description: good.description,
            image_url: good.images[0] || null,
            formated_price: price.formated_price,
          };
        })
        .sort((a, b) => (order[a.id] - order[b.id]));
    },
  },
  methods: {
    /**
     * Fetch products for broadcast id
     */
    async fetchProducts() {
      const { broadcast: { id } } = this;
      await this.$store.dispatch('Products/listBroadcastProducts', id);

      this.$nextTick(() => {
        if (this.firstRender) {
          this.firstRender = false;
        }
        this.fetching = false;
      });
    },
    /**
     * Move product up or down in the list and update broadcast with new order
     * @param {Boolean} up
     * @param {Number} index
     */
    async moveProduct(up, index) {
      const products = this.products.map(item => item.id);
      const [product] = products.splice(index, 1);
      products.splice(up ? index - 1 : index + 1, 0, product);

      await this.$store.dispatch('Products/sortProductsOnBroadcast', { id: this.broadcast.id, arr: products });
      await this.$store.dispatch('Broadcast/update', { id: this.broadcast.id, data: {} });
      this.fetchProducts();
    },
  },
  watch: {
    eventProducts(arr) {
      const showing = arr.filter(i => i.show);
      const removeItems = showing.splice(0, showing.length - 3);

      if (removeItems.length > 0) {
        removeItems.forEach((item) => {
          this.$store.dispatch('Products/toggleEventProduct', {
            id: this.broadcast.id,
            payload: {
              event: 'product.hide',
              id: item.id,
            },
          });
        });
      }
    },
  },
  data() {
    return {
      fetching: true,
      productsInterval: null,
      firstRender: true,
      addProducts: false,
      addCustomProduct: false,
    };
  },
  mounted() {
    this.fetchProducts();
    if (['ready', 'liveStarting', 'live', 'liveStopping'].includes(this.broadcast.status.life_cycle_status)) {
      this.productsInterval = setInterval(() => {
        this.fetchProducts();
      }, 30000);
    }
  },
  beforeDestroy() {
    clearInterval(this.productsInterval);
  },
};
</script>

<style lang="scss" scoped>
.products-list {
  background: #F3F5F9;
  box-shadow: inset 0 1px 2px 0 rgba(0, 0, 0, .10);
  flex: 1;
  display: none;
  flex-direction: column;
  overflow: hidden;

  &[data-show="true"] {
    display: flex;
  }

  .products {
    overflow: auto;
    padding: 10px;
    border-top: 1px solid #C1C5D7;
    flex: 1;

    & > div {
      display: flex;
      gap: 10px;
      flex-direction: column;
    }
  }
}
.product-transition-move {
  transition: transform 0.5s ease;
}
.product-transition-enter-active, .product-transition-leave-active {
  transition: opacity 1s, transform 1s;
}
.product-transition-enter, .product-transition-leave-to {
  opacity: 0;
  transform: translateX(-100px);
}

.list-info {
  display: flex;
  position: relative;
  align-items: center;
  justify-content: center;
}
</style>
